/**
 * Hostnames of the deployed environments of Nymbl Staff. All hold real users, so
 * none surfaces the demo / sandbox accounts or the "Try as — sandbox accounts"
 * login panel — see {@link isDeployedHost}.
 *
 * Both run on Nymbl Cloud since the EC2 boxes were retired (2026-10-14). The old
 * `nos-staff.nymbl.host` / `nos-staff-staging.nymbl.host` addresses only forward
 * there now, but `*.nymbl.host` still counts as deployed in case one ever serves
 * the app again.
 *
 * Users reach the deployed app on its public `*.nymbl.app` domain
 * (`staff.nymbl.app`), so every `nymbl.app` host counts as deployed too. No
 * sandbox or preview is ever served from `nymbl.app` — those live on
 * `*.nymbl.studio`.
 */
export const PRODUCTION_HOST = "nos2-staff-production.nos.nymbl.studio";
export const STAGING_HOST = "nos2-staff-staging.nos.nymbl.studio";

/** Nymbl Cloud hosts are matched exactly — every other `*.nymbl.studio` is a sandbox. */
const NYMBL_CLOUD_DEPLOYED_HOSTS: ReadonlySet<string> = new Set([PRODUCTION_HOST, STAGING_HOST]);

/**
 * True for a deployed environment — staging or production: the public
 * `*.nymbl.app` domain, the live `*.nymbl.host` app domain, or one of the
 * exact Nymbl Cloud hosts above.
 *
 * Staging counts as deployed, not as a sandbox: it is meant to look like
 * production, and its env never sets SEED_DEMO_USERS, so sandbox logins
 * there would only fail. Everything else is treated as a sandbox so the
 * demo-account affordances stay visible there:
 *
 * - local dev — `localhost`, `127.0.0.1`, `*.local`
 * - studio preview / sandbox / workstation hosts — `*.nymbl.studio`
 *   (including `*.staging.nymbl.studio`, which is a studio preview, not the
 *   staging deployment), other than the exact Nymbl Cloud hosts above
 * - any unrecognized host — fail-open (the panel only ever exposes synthetic
 *   sandbox logins, so the safe default is to show it everywhere except where
 *   we are sure it is a deployed environment)
 */
export function isDeployedHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const host = hostname.trim().toLowerCase();
  if (!host) return false;

  return (
    host === "nymbl.app" ||
    host.endsWith(".nymbl.app") ||
    host === "nymbl.host" ||
    host.endsWith(".nymbl.host") ||
    NYMBL_CLOUD_DEPLOYED_HOSTS.has(host)
  );
}

/**
 * True for a host we KNOW is a preview: local development (`localhost`, `127.0.0.1`,
 * `*.local`) or a studio preview / sandbox / workstation host (`*.nymbl.studio`), and
 * never a deployed one. Unlike the login panel's fail-open check above, an unrecognised
 * host is NOT a preview here: this decides whether the demo accounts are created, and
 * those carry a published password, so it has to be sure.
 */
export function isPreviewHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const host = hostname.trim().toLowerCase();
  if (!host || isDeployedHost(host)) return false;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local") ||
    host === "nymbl.studio" ||
    host.endsWith(".nymbl.studio")
  );
}
